import { Suspense } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import './features/participant-survey/styles/mobile-improvements.css'
import { ProtectedRoute } from './features/auth/components/ProtectedRoute'
import { RequireRole } from './features/auth/components/RequireRole'
import { AuthRoute } from './features/auth/components/AuthRoute'
import { RBACMainLayout } from './components/layout/rbac-main-layout'
import { useRBACInitialization } from './hooks/rbac/useRBACInitialization'
import { RBACErrorHandler } from './components/common/RBACErrorHandler'
import { NotFoundPage } from './components/common/NotFoundPage'
import { CreateAssessmentRouter } from './components/CreateAssessmentRouter'
import {
  // Authentication
  LoginPage,
  ChangePasswordPage,
  AutoLoginPage,
  // Dashboard
  DashboardPage,
  // Survey/Assessment
  AssessmentPage,
  EditAssessmentPage,
  ReadAssessmentDetails,
  // Participants
  AddParticipants,
  EditParticipant,
  ParticipantSearchPage,
  ParticipantPrioritySearchPage,
  // Organization
  UpdateOrganizationPage,
  ManageChampionsPage,
  ListGroupPage,
  UpdateGroupPage,
  CreateGroupPage,
  CreateOrganizationPage,
  ListDistrictsPage,
  // Admin
  AdminUsersPage,
  IndicatorsListPage,
  // Reports
  OverallReportPage,
  CombinedReportPage,
  // Guidelines
  GuidelinePage,
  // Participant Survey (public)
  ParticipantAccessPage,
  ParticipantIntroPage,
  ParticipantVerifyPage,
  ParticipantAnonymousPage,
  ParticipantConsentPage,
  ParticipantDemographicsPage,
  ParticipantQuestionsPage,
  ParticipantCompletePage,
  ParticipantSelfRegistrationPage,
  ProctorPage,
  // Wellness Center (public)
  WellnessCenterAccessPage,
  WellnessCenterHomePage,
  WellnessCenterReportPage,
  WellnessCenterVerification
} from './components/lazy/LazyRoutes'
import {
  LazyLoadingFallback,
  SurveyLoadingFallback,
  WellnessCenterLoadingFallback,
  DashboardLoadingFallback,
  AssessmentLoadingFallback,
  ParticipantLoadingFallback,
  OrganizationLoadingFallback,
  AdminLoadingFallback,
  ReportLoadingFallback
} from './components/common/LazyLoadingFallback'

function App() {
  const location = useLocation();
  
  // Check if current route is public (doesn't need authentication)
  const isPublicRoute = location.pathname.startsWith('/wellness-center') || 
                        location.pathname.startsWith('/survey');

  // Only initialize RBAC system for non-public routes
  const rbacState = useRBACInitialization();
  const { 
    authLoading, 
    error: rbacError,
    retryCount,
    maxRetries,
    isRetrying,
    retry,
    clearError
  } = isPublicRoute ? {
    authLoading: false,
    error: null,
    retryCount: 0,
    maxRetries: 3,
    isRetrying: false,
    retry: () => Promise.resolve(),
    clearError: () => {}
  } : rbacState;

  // Only log RBAC status when there are errors or important state changes (and not on public routes)
  if (rbacError && !isPublicRoute) {
    console.warn('⚠️ App: RBAC Error detected', {
      errorCode: rbacError?.code,
      retryCount
    });
  }

  // Show RBAC error if initialization failed (but not for public routes)
  if (rbacError && !authLoading && !isPublicRoute) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-2xl mx-auto">
          <RBACErrorHandler
            error={rbacError}
            retryCount={retryCount}
            maxRetries={maxRetries}
            isRetrying={isRetrying}
            onRetry={retry}
            onClearError={clearError}
            className="mt-8"
          />
        </div>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Participant Survey Routes - No authentication required */}
        <Route path="/survey" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantAccessPage />
          </Suspense>
        } />
        <Route path="/survey/intro" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantIntroPage />
          </Suspense>
        } />
        <Route path="/survey/verify" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantVerifyPage />
          </Suspense>
        } />
        <Route path="/survey/anonymous" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantAnonymousPage />
          </Suspense>
        } />
        <Route path="/survey/consent" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantConsentPage />
          </Suspense>
        } />
        <Route path="/survey/demographics" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantDemographicsPage />
          </Suspense>
        } />
        <Route path="/survey/questions" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantQuestionsPage />
          </Suspense>
        } />
        <Route path="/survey/complete" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantCompletePage />
          </Suspense>
        } />
        <Route path="/survey/proctor/:encodedList?" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ProctorPage />
          </Suspense>
        } />
        
        {/* Self Registration Route - No authentication required */}
        <Route path="/self/:surveyId/add" element={
          <Suspense fallback={<SurveyLoadingFallback />}>
            <ParticipantSelfRegistrationPage />
          </Suspense>
        } />
        
        {/* Wellness Center Routes - No authentication required */}
        <Route path="/wellness-center" element={
          <Suspense fallback={<WellnessCenterLoadingFallback />}>
            <WellnessCenterAccessPage />
          </Suspense>
        } />
        <Route path="/wellness-center/result" element={
          <Suspense fallback={<WellnessCenterLoadingFallback />}>
            <WellnessCenterVerification />
          </Suspense>
        } />
        <Route path="/wellness-center/home" element={
          <Suspense fallback={<WellnessCenterLoadingFallback />}>
            <WellnessCenterHomePage />
          </Suspense>
        } />
        <Route path="/wellness-center/report/:template/:surveyParticipantId" element={
          <Suspense fallback={<WellnessCenterLoadingFallback />}>
            <WellnessCenterReportPage />
          </Suspense>
        } />
        
        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        
        {/* Admin Authentication Route */}
        <Route
          path="/login"
          element={
            <AuthRoute>
              <Suspense fallback={<LazyLoadingFallback message="Loading login..." />}>
                <LoginPage />
              </Suspense>
            </AuthRoute>
          }
        />
        <Route
          path="/login/"
          element={
            <AuthRoute>
              <Suspense fallback={<LazyLoadingFallback message="Loading login..." />}>
                <LoginPage />
              </Suspense>
            </AuthRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <Suspense fallback={<LazyLoadingFallback message="Loading..." />}>
              <ChangePasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/auto-login"
          element={
            <Suspense fallback={<LazyLoadingFallback message="Authenticating..." />}>
              <AutoLoginPage />
            </Suspense>
          }
        />
        
        {/* Protected Admin Routes with RBAC */}
        <Route
          element={
            <ProtectedRoute>
              <RBACMainLayout>
                <Outlet />
              </RBACMainLayout>
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={
            <Suspense fallback={<DashboardLoadingFallback />}>
              <DashboardPage />
            </Suspense>
          } />
          <Route path="/guideline" element={
            <Suspense fallback={<LazyLoadingFallback message="Loading guidelines..." />}>
              <GuidelinePage />
            </Suspense>
          } />
          <Route path="/assessment" element={
            <Suspense fallback={<AssessmentLoadingFallback />}>
              <AssessmentPage />
            </Suspense>
          } />
          <Route path="/assessment/create" element={
            <Suspense fallback={<AssessmentLoadingFallback />}>
              <CreateAssessmentRouter />
            </Suspense>
          } />
          <Route path="/assessment/compare" element={
            <Suspense fallback={<ReportLoadingFallback />}>
              <OverallReportPage />
            </Suspense>
          } />
          <Route path="/assessment/combined-report" element={
            <RequireRole routePath="/assessment/combined-report">
              <Suspense fallback={<ReportLoadingFallback />}>
                <CombinedReportPage />
              </Suspense>
            </RequireRole>
          } />
          <Route path="/assessment/edit/:surveyId" element={
            <Suspense fallback={<AssessmentLoadingFallback />}>
              <EditAssessmentPage />
            </Suspense>
          } />
          <Route path="/assessment/details/:surveyId" element={
            <Suspense fallback={<AssessmentLoadingFallback />}>
              <ReadAssessmentDetails />
            </Suspense>
          } />
          <Route path="/assessment/details/:surveyId/add" element={
            <Suspense fallback={<ParticipantLoadingFallback />}>
              <AddParticipants />
            </Suspense>
          } />
          <Route path="/assessment/details/:surveyId/edit/:participantId" element={
            <Suspense fallback={<ParticipantLoadingFallback />}>
              <EditParticipant />
            </Suspense>
          } />
          <Route path="/participants/search" element={
            <Suspense fallback={<ParticipantLoadingFallback />}>
              <ParticipantSearchPage />
            </Suspense>
          } />
          <Route path="/participants/priority" element={
            <Suspense fallback={<ParticipantLoadingFallback />}>
              <ParticipantPrioritySearchPage />
            </Suspense>
          } />
          <Route path="/organization/list" element={
            <Suspense fallback={<OrganizationLoadingFallback />}>
              <ListDistrictsPage />
            </Suspense>
          } />
          <Route path="/organization/create" element={
            <Suspense fallback={<OrganizationLoadingFallback />}>
              <CreateOrganizationPage />
            </Suspense>
          } />
          <Route path="/organization/update" element={
            <Suspense fallback={<OrganizationLoadingFallback />}>
              <UpdateOrganizationPage />
            </Suspense>
          } />
          <Route path="/organization/manage-champions" element={
            <Suspense fallback={<OrganizationLoadingFallback />}>
              <ManageChampionsPage />
            </Suspense>
          } />
          <Route path="/group/list" element={
            <Suspense fallback={<OrganizationLoadingFallback />}>
              <ListGroupPage />
            </Suspense>
          } />
          <Route path="/group/create" element={
            <Suspense fallback={<OrganizationLoadingFallback />}>
              <CreateGroupPage />
            </Suspense>
          } />
          <Route path="/group/update" element={
            <Suspense fallback={<OrganizationLoadingFallback />}>
              <UpdateGroupPage />
            </Suspense>
          } />
          <Route path="/admin/users" element={
            <Suspense fallback={<AdminLoadingFallback />}>
              <AdminUsersPage />
            </Suspense>
          } />
          <Route path="/admin/indicators" element={
            <Suspense fallback={<AdminLoadingFallback />}>
              <IndicatorsListPage />
            </Suspense>
          } />
        </Route>
        
        {/* Catch-all route for 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AnimatePresence>
  )
}

export default App
